import {
  ASSET_FINANCE_MAX_BROKERAGE_PERCENT,
  BUSINESS_LOAN_MAX_BROKERAGE_PERCENT,
  BUSINESS_OVERDRAFT_MAX_BROKERAGE_PERCENT,
} from '../const';
import { ApplicationTypes } from '../domain/application-service.domain';
import { getLocaleNumber } from './utils';

// $1 allowance for rounding differences between the pricing and contract loan bases
const BROKERAGE_CAP_TOLERANCE = 1;

// [LEGACY-8PCT] START — Asset Finance deals created before 00:00 Australia/Sydney on 14 July 2026
// (AEST, UTC+10) were approved under the temporary 8% brokerage allowance and may still generate
// contracts at up to 8%. Delete this block and every [LEGACY-8PCT] usage after 15/11/2026, when the
// approval notices for these deals expire.
const LEGACY_ASSET_FINANCE_MAX_BROKERAGE_PERCENT = 8;
const LEGACY_ASSET_FINANCE_BROKERAGE_CUTOFF_MS = Date.parse('2026-07-13T14:00:00.000Z');

const isLegacyAssetFinanceBrokerageDeal = (createTime: string | Date | null | undefined): boolean => {
  if (createTime == null || createTime === '') {
    return false;
  }
  const ms = createTime instanceof Date ? createTime.getTime() : Date.parse(String(createTime));
  return Number.isFinite(ms) && ms < LEGACY_ASSET_FINANCE_BROKERAGE_CUTOFF_MS;
}
// [LEGACY-8PCT] END

/**
 * Loan amount that Asset Finance brokerage % is measured against.
 * Rental deposits are GST inclusive, so rentals use the ex-GST deposit.
 */
export const assetFinanceBrokerageBasis = (invoicePrice: number, deposit: number, isRental: boolean): number =>
  isRental
    ? Math.round(100 * (invoicePrice - deposit * 10 / 11)) / 100
    : invoicePrice - deposit;

export const brokerageAmountFromPercent = (percent: number, basis: number): number =>
  Math.round(basis * percent) / 100;

/** Null for products without a brokerage cap. `createTime` is the application's CreateTime. */
export const maxBrokeragePercent = (applicationType: ApplicationTypes, createTime?: string | Date | null): number | null => {
  switch (applicationType) {
    case 'AssetFinance':
      // [LEGACY-8PCT]
      return isLegacyAssetFinanceBrokerageDeal(createTime)
        ? LEGACY_ASSET_FINANCE_MAX_BROKERAGE_PERCENT
        : ASSET_FINANCE_MAX_BROKERAGE_PERCENT;
    case 'BusinessLoans':
      return BUSINESS_LOAN_MAX_BROKERAGE_PERCENT;
    case 'BusinessOverdraft':
      return BUSINESS_OVERDRAFT_MAX_BROKERAGE_PERCENT;
  }
  return null;
}

export const maxBrokerageAmount = (applicationType: ApplicationTypes, basis: number, createTime?: string | Date | null): number | null => {
  const percent = maxBrokeragePercent(applicationType, createTime);
  return percent == null ? null : brokerageAmountFromPercent(percent, basis);
}

export const brokerageExceedsMax = (applicationType: ApplicationTypes, brokerageAmount: number, basis: number, createTime?: string | Date | null): boolean => {
  const max = maxBrokerageAmount(applicationType, basis, createTime);
  return max != null && brokerageAmount > max + BROKERAGE_CAP_TOLERANCE;
}

export const brokerageExceedsMaxMessage = (applicationType: ApplicationTypes, brokerageAmount: number, basis: number, createTime?: string | Date | null): string => {
  const percent = basis ? (brokerageAmount * 100 / basis).toFixed(2) : '0.00';
  return `Brokerage $${getLocaleNumber(brokerageAmount)} (${percent}%) exceeds the maximum `
    + `${maxBrokeragePercent(applicationType, createTime)}% ($${getLocaleNumber(maxBrokerageAmount(applicationType, basis, createTime) ?? 0)})`;
}
